import { useEffect, useRef, useState, useCallback } from 'react';
import type { Difficulty } from '../../utils/mathProblems';
import type { BallState } from './TennisFirstPerson';

interface Props {
  onRallyEnd: (winner: 'player' | 'opponent') => void;
  difficulty: Difficulty;
  mode?: 'vs-cpu' | 'vs-player';
  // Online sync props
  isGuest?: boolean;
  externalBall?: BallState | null;
  remoteOpponentX?: number;
  onBallUpdate?: (b: BallState) => void;
  onPaddleUpdate?: (x: number) => void;
  opponentLabel?: string;
}

// ── Canvas & perspective constants ──────────────────────────────────────
const CW = 720;
const CH = 470;
const HORIZON_Y = 138;
const BASELINE_Y = CH - 48;
const HW_FAR = 76;
const HW_NEAR = 268;

// court coords: x ∈ [-1,1], z ∈ [0=player, 1=cpu]
const SINGLES_X = 0.82;
const NET_Z = 0.5;
const NET_H = 0.19; // net height in court units

const GRAVITY = 0.009;
const BOUNCE_REST = 0.5;
const PLAYER_HIT_RANGE = 0.28; // how close player must be to ball x
const PLAYER_SPEED = 0.025;

const CPU_SPEEDS: Record<Difficulty, number> = {
  easy: 0.014, medium: 0.02, hard: 0.028, expert: 0.038,
};
const CPU_ERRORS: Record<Difficulty, number> = {
  easy: 0.32, medium: 0.18, hard: 0.07, expert: 0.01,
};

// ── Perspective projection ───────────────────────────────────────────────
function project(x: number, z: number, height = 0) {
  const t = 1 - z;
  const groundY = HORIZON_Y + (BASELINE_Y - HORIZON_Y) * t;
  const hw = HW_FAR + (HW_NEAR - HW_FAR) * t;
  const scale = t * 0.72 + 0.28;
  return {
    sx: CW / 2 + x * hw,
    sy: groundY - height * 195 * scale,
    groundY,
    scale,
  };
}

// ── Ball state ───────────────────────────────────────────────────────────
interface Ball {
  x: number; z: number; h: number;
  vx: number; vz: number; vh: number;
  bounced: boolean;
  lastHitter: 'player' | 'cpu';
}

type Announce = { text: string; color: string; sub?: string } | null;

function makeShot(
  fromZ: number,
  fromX: number,
  targetX: number,
  hitter: 'player' | 'cpu',
  error: number
): Ball {
  const jitter = (Math.random() - 0.5) * error * 1.6;
  const finalX = Math.max(-0.95, Math.min(0.95, targetX + jitter));
  const dx = finalX - fromX;
  const dz = hitter === 'player' ? 1 : -1;
  const speed = 0.017 + Math.random() * 0.004;
  return {
    x: fromX, z: fromZ, h: 0.04,
    vx: dx * speed * 1.1,
    vz: dz * speed,
    vh: 0.14 + Math.random() * 0.04,
    bounced: false,
    lastHitter: hitter,
  };
}

// ── Drawing helpers ──────────────────────────────────────────────────────
function drawCourt(ctx: CanvasRenderingContext2D) {
  // Sky/background
  const bg = ctx.createLinearGradient(0, 0, 0, CH);
  bg.addColorStop(0, '#0f4c2a');
  bg.addColorStop(0.3, '#145e33');
  bg.addColorStop(1, '#1a7a41');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CW, CH);

  // Far end darker fade
  const fade = ctx.createLinearGradient(0, HORIZON_Y, 0, BASELINE_Y);
  fade.addColorStop(0, 'rgba(0,0,0,0.25)');
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, HORIZON_Y, CW, BASELINE_Y - HORIZON_Y);

  const line = (x1: number, z1: number, x2: number, z2: number, alpha = 0.85, dashed = false) => {
    const a = project(x1, z1);
    const b = project(x2, z2);
    ctx.save();
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    ctx.lineWidth = 1.8;
    if (dashed) ctx.setLineDash([8, 7]);
    ctx.beginPath();
    ctx.moveTo(a.sx, a.groundY);
    ctx.lineTo(b.sx, b.groundY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  };

  // Doubles sidelines (out zone indicator - slightly transparent red tint sides)
  // Singles sidelines
  line(-SINGLES_X, 0, -SINGLES_X, 1);
  line(SINGLES_X, 0, SINGLES_X, 1);
  // Baselines
  line(-1, 0, 1, 0);
  line(-1, 1, 1, 1);
  // Center service line
  line(0, NET_Z, 0, 0.78, 0.45, true);
  line(0, NET_Z, 0, 0.22, 0.45, true);
  // Service lines
  line(-SINGLES_X, 0.22, SINGLES_X, 0.22, 0.5);
  line(-SINGLES_X, 0.78, SINGLES_X, 0.78, 0.5);

  // "OUT" zones (subtle pink sides) - painted on court surface
  const pLeft = [
    project(-1, 0), project(-SINGLES_X, 0),
    project(-SINGLES_X, 1), project(-1, 1),
  ];
  const pRight = [
    project(SINGLES_X, 0), project(1, 0),
    project(1, 1), project(SINGLES_X, 1),
  ];
  for (const pts of [pLeft, pRight]) {
    ctx.beginPath();
    ctx.moveTo(pts[0].sx, pts[0].groundY);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].sx, pts[i].groundY);
    ctx.closePath();
    ctx.fillStyle = 'rgba(220,30,30,0.12)';
    ctx.fill();
  }

  // Net
  const netL = project(-1, NET_Z, NET_H);
  const netR = project(1, NET_Z, NET_H);
  const netLg = project(-1, NET_Z, 0);
  const netRg = project(1, NET_Z, 0);
  // Net fill
  ctx.fillStyle = 'rgba(200,200,200,0.18)';
  ctx.beginPath();
  ctx.moveTo(netLg.sx, netLg.groundY);
  ctx.lineTo(netRg.sx, netRg.groundY);
  ctx.lineTo(netR.sx, netR.sy);
  ctx.lineTo(netL.sx, netL.sy);
  ctx.closePath();
  ctx.fill();
  // Net top
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(netL.sx, netL.sy);
  ctx.lineTo(netR.sx, netR.sy);
  ctx.stroke();
  // Net lines (horizontal)
  for (let i = 1; i <= 4; i++) {
    const nh = (NET_H * i) / 5;
    const nL = project(-1, NET_Z, nh);
    const nR = project(1, NET_Z, nh);
    ctx.strokeStyle = `rgba(255,255,255,${0.15 + i * 0.04})`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(nL.sx, nL.sy); ctx.lineTo(nR.sx, nR.sy);
    ctx.stroke();
  }
  // Net posts
  for (const nx of [-1, 1]) {
    const pg = project(nx, NET_Z, 0);
    const pt = project(nx, NET_Z, NET_H + 0.04);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(pg.sx, pg.groundY); ctx.lineTo(pt.sx, pt.sy);
    ctx.stroke();
  }
}

function drawShadow(ctx: CanvasRenderingContext2D, ball: Ball) {
  const p = project(ball.x, ball.z, 0);
  const r = 7 + p.scale * 8;
  const g = ctx.createRadialGradient(p.sx, p.groundY, 0, p.sx, p.groundY, r);
  g.addColorStop(0, `rgba(0,0,0,${0.5 - ball.h * 0.4})`);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.ellipse(p.sx, p.groundY, r, r * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawBall(ctx: CanvasRenderingContext2D, ball: Ball) {
  const p = project(ball.x, ball.z, ball.h);
  const r = (5 + p.scale * 9);
  ctx.shadowColor = '#fde047';
  ctx.shadowBlur = 10 * p.scale;
  ctx.fillStyle = '#d4e84a';
  ctx.beginPath();
  ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Seam
  ctx.strokeStyle = 'rgba(60,100,20,0.6)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(p.sx, p.sy, r * 0.6, 0.3, Math.PI - 0.3);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(p.sx, p.sy, r * 0.6, Math.PI + 0.3, -0.3);
  ctx.stroke();
  // Highlight
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.beginPath();
  ctx.arc(p.sx - r * 0.25, p.sy - r * 0.28, r * 0.28, 0, Math.PI * 2);
  ctx.fill();
}

function drawPlayer(ctx: CanvasRenderingContext2D, px: number, color: string, label: string, isPlayer: boolean) {
  const z = isPlayer ? 0.0 : 1.0;
  const p = project(px, z, 0);
  const pw = 22 * p.scale;
  const ph = 56 * p.scale;
  // Body
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(p.sx - pw / 2, p.groundY - ph, pw, ph, 4 * p.scale);
  ctx.fill();
  // Racket arm
  ctx.strokeStyle = color;
  ctx.lineWidth = 4 * p.scale;
  ctx.beginPath();
  ctx.moveTo(p.sx + pw / 2, p.groundY - ph * 0.6);
  ctx.lineTo(p.sx + pw / 2 + 14 * p.scale, p.groundY - ph * 0.5);
  ctx.stroke();
  // Racket head
  ctx.strokeStyle = 'rgba(255,255,255,0.8)';
  ctx.lineWidth = 2 * p.scale;
  ctx.beginPath();
  ctx.ellipse(p.sx + pw / 2 + 18 * p.scale, p.groundY - ph * 0.5, 9 * p.scale, 12 * p.scale, -0.3, 0, Math.PI * 2);
  ctx.stroke();
  // Head
  ctx.fillStyle = '#fcd5a2';
  ctx.beginPath();
  ctx.arc(p.sx, p.groundY - ph - 9 * p.scale, 9 * p.scale, 0, Math.PI * 2);
  ctx.fill();
  // Label
  ctx.font = `bold ${Math.round(11 * p.scale + 5)}px "Bebas Neue", sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.textAlign = 'center';
  ctx.letterSpacing = '1px';
  ctx.fillText(label, p.sx, p.groundY + 16 * p.scale);
}

// ── Main component ──────────────────────────────────────────────────────
export default function TennisMiniGame({ onRallyEnd, difficulty, mode = 'vs-cpu', isGuest = false, externalBall, remoteOpponentX, onBallUpdate, onPaddleUpdate, opponentLabel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [countdown, setCountdown] = useState(3);
  const [started, setStarted] = useState(false);
  const [result, setResult] = useState<'player' | 'opponent' | null>(null);
  const [announce, setAnnounce] = useState<Announce>(null);

  const playerX = useRef(0);
  const cpuX = useRef(0);
  const ball = useRef<Ball | null>(null);
  const keys = useRef<Record<string, boolean>>({});
  const running = useRef(false);
  const done = useRef(false);
  const frameRef = useRef(0);

  // Online sync refs
  const isOnline = !!onBallUpdate || !!externalBall;
  const onBallUpdateRef = useRef(onBallUpdate);
  const onPaddleUpdateRef = useRef(onPaddleUpdate);
  const remoteXRef = useRef(remoteOpponentX ?? 0);
  useEffect(() => { onBallUpdateRef.current = onBallUpdate; }, [onBallUpdate]);
  useEffect(() => { onPaddleUpdateRef.current = onPaddleUpdate; }, [onPaddleUpdate]);
  useEffect(() => { remoteXRef.current = remoteOpponentX ?? 0; }, [remoteOpponentX]);

  const cpuSpeed = CPU_SPEEDS[difficulty];
  const cpuError = CPU_ERRORS[difficulty];

  // Labels: in online mode guest sees themselves at the top (p2 position)
  const p1Label = isOnline && isGuest ? (opponentLabel ?? 'HOST') : (mode === 'vs-player' ? 'P1' : 'YOU');
  const p2Label = isOnline && isGuest ? 'YOU' : (mode === 'vs-player' ? 'P2' : (opponentLabel ?? 'CPU'));

  const endRally = useCallback((winner: 'player' | 'opponent', ann: Announce) => {
    if (done.current) return;
    done.current = true;
    running.current = false;
    setAnnounce(ann);
    setResult(winner);
  }, []);

  // Countdown — guest waits for externalBall, host/local creates initial ball
  useEffect(() => {
    if (countdown <= 0) {
      if (!isGuest) {
        ball.current = makeShot(1, 0, (Math.random() - 0.5) * 0.7, 'cpu', cpuError * 0.5);
        onBallUpdateRef.current?.(ball.current);
      }
      setStarted(true);
      running.current = true;
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 900);
    return () => clearTimeout(t);
  }, [countdown, cpuError, isGuest]);

  // Sync external ball (guest only)
  useEffect(() => {
    if (isGuest && externalBall) ball.current = { ...externalBall };
  }, [isGuest, externalBall]);

  // Key events
  useEffect(() => {
    const dn = (e: KeyboardEvent) => {
      keys.current[e.key] = true;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'q', 'Q', 'e', 'E'].includes(e.key)) e.preventDefault();
    };
    const up = (e: KeyboardEvent) => { keys.current[e.key] = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  // Game loop
  useEffect(() => {
    if (!started) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const loop = () => {
      ctx.clearRect(0, 0, CW, CH);
      drawCourt(ctx);

      const b = ball.current;
      if (b) {
        drawShadow(ctx, b);
        drawBall(ctx, b);
      }
      drawPlayer(ctx, playerX.current, '#84cc16', p1Label, true);
      drawPlayer(ctx, cpuX.current, '#f97316', p2Label, false);

      // HUD label
      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.textAlign = 'center';
      ctx.letterSpacing = '2px';
      ctx.fillText('3RD PERSON VIEW', CW / 2, 18);
      ctx.letterSpacing = '0px';

      frameRef.current++;

      if (running.current && b) {
        const k = keys.current;

        if (isOnline && isGuest) {
          // Guest: controls top paddle (cpuX = their own position)
          if ((k['a'] || k['A'] || k['ArrowLeft']) && cpuX.current > -0.88) cpuX.current -= PLAYER_SPEED;
          if ((k['d'] || k['D'] || k['ArrowRight']) && cpuX.current < 0.88) cpuX.current += PLAYER_SPEED;
          // Sync opponent (host) position from remote
          playerX.current = remoteXRef.current;
          // Broadcast own paddle every 3 frames
          if (frameRef.current % 3 === 0) onPaddleUpdateRef.current?.(cpuX.current);
        } else {
          // Host or local: controls bottom paddle (playerX)
          if ((k['a'] || k['A'] || k['ArrowLeft']) && playerX.current > -0.88) playerX.current -= PLAYER_SPEED;
          if ((k['d'] || k['D'] || k['ArrowRight']) && playerX.current < 0.88) playerX.current += PLAYER_SPEED;

          if (isOnline) {
            // Sync opponent (guest) position from remote
            cpuX.current = remoteXRef.current;
          } else if (mode === 'vs-player') {
            // Player 2 movement: Q/E
            if ((k['q'] || k['Q']) && cpuX.current > -0.88) cpuX.current -= PLAYER_SPEED;
            if ((k['e'] || k['E']) && cpuX.current < 0.88) cpuX.current += PLAYER_SPEED;
          } else {
            // CPU AI - track ball when it's coming toward cpu
            if (b.vz > 0 && b.z > 0.3) {
              const target = b.x;
              const diff = target - cpuX.current;
              cpuX.current += Math.sign(diff) * Math.min(Math.abs(diff), cpuSpeed);
            }
          }

          // Broadcast own paddle every 3 frames (host online only)
          if (isOnline && frameRef.current % 3 === 0) onPaddleUpdateRef.current?.(playerX.current);

          // Physics (only host or local runs physics)
          b.z += b.vz;
          b.x += b.vx;
          b.h += b.vh;
          b.vh -= GRAVITY;

          // ── Net check ─────────────────────────────────────────────────
          const crossingNet = (b.lastHitter === 'player' && b.z >= NET_Z && b.z - b.vz < NET_Z) ||
            (b.lastHitter === 'cpu' && b.z <= NET_Z && b.z - b.vz > NET_Z);
          if (crossingNet && b.h < NET_H) {
            const loser = b.lastHitter;
            endRally(
              loser === 'player' ? 'opponent' : 'player',
              {
                text: 'NET!',
                color: '#f97316',
                sub: loser === 'player'
                  ? `${p1Label} hit the net — ${p2Label} wins!`
                  : `${p2Label} hit the net — ${p1Label} wins!`,
              }
            );
          }

          // ── Ball bounce ────────────────────────────────────────────────
          if (b.h <= 0 && !done.current) {
            b.h = 0;
            const isOut = Math.abs(b.x) > SINGLES_X;
            const isPastPlayerBaseline = b.z < 0;
            const isPastCpuBaseline = b.z > 1;

            if (isOut) {
              const loser = b.lastHitter;
              endRally(loser === 'player' ? 'opponent' : 'player', {
                text: 'OUT!',
                color: '#ef4444',
                sub: loser === 'player'
                  ? `${p1Label}'s shot went wide — ${p2Label} wins!`
                  : `${p2Label}'s shot went out — ${p1Label} wins!`,
              });
            } else if (isPastPlayerBaseline) {
              endRally('opponent', { text: 'OUT!', color: '#ef4444', sub: `Ball passed baseline — ${p2Label} wins!` });
            } else if (isPastCpuBaseline) {
              endRally('player', { text: 'OUT!', color: '#ef4444', sub: `Ball passed ${p2Label} baseline — ${p1Label} wins!` });
            } else if (!b.bounced) {
              b.bounced = true;
              b.vh = Math.abs(b.vh) * BOUNCE_REST;
              b.vx *= 0.82;
            } else {
              if (b.vz < 0 && b.z < 0.5) {
                endRally('opponent', { text: `POINT ${p2Label}!`, color: '#f97316', sub: `Ball bounced twice — ${p2Label} wins the rally` });
              } else if (b.vz > 0 && b.z > 0.5) {
                endRally('player', { text: `POINT ${p1Label}!`, color: '#84cc16', sub: `Ball bounced twice on ${p2Label} side — ${p1Label} wins!` });
              } else {
                b.vh = Math.abs(b.vh) * BOUNCE_REST * 0.4;
              }
            }
          }

          // ── Player 1 hit zone ──────────────────────────────────────────
          if (!done.current && b.vz < 0 && b.z <= 0.04 && b.z >= -0.06 && b.lastHitter === 'cpu') {
            const dist = Math.abs(b.x - playerX.current);
            if (dist <= PLAYER_HIT_RANGE) {
              const aim = playerX.current < b.x ? b.x - 0.3 : b.x + 0.3;
              const clamped = Math.max(-0.9, Math.min(0.9, aim + (Math.random() - 0.5) * 0.1));
              ball.current = makeShot(0, b.x, clamped, 'player', 0.06);
            } else {
              endRally('opponent', { text: 'MISS!', color: '#f97316', sub: `${p1Label} didn't reach the ball — ${p2Label} wins!` });
            }
          }

          // ── Player 2 / CPU hit zone ────────────────────────────────────
          if (!done.current && b.vz > 0 && b.z >= 0.96 && b.z <= 1.06 && b.lastHitter === 'player') {
            const dist = Math.abs(b.x - cpuX.current);
            if (mode === 'vs-player' || isOnline) {
              // P2 or online guest must physically be in range
              if (dist <= PLAYER_HIT_RANGE) {
                const aim = cpuX.current < b.x ? b.x - 0.3 : b.x + 0.3;
                const clamped = Math.max(-0.9, Math.min(0.9, aim + (Math.random() - 0.5) * 0.1));
                ball.current = makeShot(1, b.x, clamped, 'cpu', 0.06);
              } else {
                endRally('player', { text: 'MISS!', color: '#84cc16', sub: `${p2Label} didn't reach the ball — ${p1Label} wins!` });
              }
            } else {
              const cpuRange = PLAYER_HIT_RANGE * (1.1 - cpuError);
              if (dist <= cpuRange) {
                const aim = (Math.random() - 0.5) * 1.1;
                ball.current = makeShot(1, b.x, aim, 'cpu', cpuError);
              } else {
                endRally('player', { text: 'CPU MISS!', color: '#84cc16', sub: 'CPU couldn\'t reach it — You win the rally!' });
              }
            }
          }

          // Broadcast ball state every 2 frames (host/local online)
          if (isOnline && frameRef.current % 2 === 0 && ball.current) {
            onBallUpdateRef.current?.(ball.current);
          }
        }
      }

      // HUD OUT zones
      if (started && !result) {
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = 'rgba(255,80,80,0.6)';
        ctx.textAlign = 'center';
        ctx.letterSpacing = '2px';
        ctx.fillText('OUT', CW / 2 - HW_NEAR - 22, BASELINE_Y - 10);
        ctx.fillText('OUT', CW / 2 + HW_NEAR + 22, BASELINE_Y - 10);
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, [started, cpuSpeed, cpuError, endRally, result, mode, p1Label, p2Label, isGuest, isOnline]);

  // Auto-proceed after result
  useEffect(() => {
    if (!result) return;
    const delay = isOnline ? 1400 : 2400;
    const t = setTimeout(() => onRallyEnd(result), delay);
    return () => clearTimeout(t);
  }, [result, onRallyEnd, isOnline]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center">
        <p className="text-green-700 font-semibold text-sm">
          <i className="ri-checkbox-circle-line mr-1 text-green-500" />
          {isOnline && isGuest
            ? 'You\'re at the top — move into position and return the shot!'
            : 'Correct! Move into position and return the shot to win the point!'}
        </p>
      </div>

      <div className="relative rounded-2xl overflow-hidden border-2 border-green-800" style={{ width: CW, height: CH }}>
        <canvas ref={canvasRef} width={CW} height={CH} style={{ display: 'block' }} />

        {/* Countdown overlay */}
        {!started && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55">
            <div
              className="font-bold text-white leading-none"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: countdown === 0 ? '90px' : '120px', letterSpacing: '0.05em' }}
            >
              {countdown === 0 ? 'GO!' : countdown}
            </div>
            {isOnline ? (
              <>
                <p className="text-green-300 text-sm mt-3 font-medium">
                  {isGuest ? 'You\'re at the TOP — A/D or ←/→ to move' : 'You\'re at the BOTTOM — A/D or ←/→ to move'}
                </p>
                <p className="text-green-400 text-xs mt-1">Stay inside the lines — OUT zones are red!</p>
              </>
            ) : mode === 'vs-player' ? (
              <>
                <p className="text-green-300 text-sm mt-3 font-medium">P1: A/D or ←/→ to move &nbsp;|&nbsp; P2: Q/E to move</p>
                <p className="text-green-400 text-xs mt-1">Stay inside the lines — OUT zones are red!</p>
              </>
            ) : (
              <>
                <p className="text-green-300 text-sm mt-3 font-medium">Use A/D or ←/→ arrows to move</p>
                <p className="text-green-400 text-xs mt-1">Stay inside the lines — OUT zones are red!</p>
              </>
            )}
          </div>
        )}

        {/* Announce overlay */}
        {announce && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
            <div
              className="font-bold leading-none drop-shadow-xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '96px', letterSpacing: '0.08em', color: announce.color }}
            >
              {announce.text}
            </div>
            {announce.sub && (
              <p className="text-white text-base mt-3 font-medium text-center max-w-xs">{announce.sub}</p>
            )}
            {result && (
              <div
                className="mt-4 px-5 py-1.5 rounded-full font-bold text-sm"
                style={{ background: result === 'player' ? '#84cc16' : '#f97316', color: '#fff', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em', fontSize: '20px' }}
              >
                {result === 'player' ? `✦ ${p1Label} WINS THE RALLY ✦` : `✦ ${p2Label} WINS THE RALLY ✦`}
              </div>
            )}
            <p className="text-gray-400 text-xs mt-3">Continuing in a moment…</p>
          </div>
        )}
      </div>

      {/* Controls hint */}
      {isOnline ? (
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="font-medium">Move ({isGuest ? 'Top' : 'Bottom'}):</span>
          {['A', 'D'].map(k => (
            <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
          ))}
          <span>or</span>
          {['←', '→'].map(k => (
            <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
          ))}
          <span className="text-red-400 font-medium ml-2">
            <i className="ri-alert-line mr-1" />Red zones = OUT
          </span>
        </div>
      ) : mode === 'vs-player' ? (
        <div className="flex items-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-lime-600">P1:</span>
            {['A', 'D'].map(k => (
              <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
            ))}
            <span>or</span>
            {['←', '→'].map(k => (
              <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
            ))}
          </div>
          <div className="w-px h-4 bg-gray-300" />
          <div className="flex items-center gap-2">
            <span className="font-semibold text-orange-500">P2:</span>
            {['Q', 'E'].map(k => (
              <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
            ))}
          </div>
          <span className="text-red-400 font-medium ml-2">
            <i className="ri-alert-line mr-1" />
            Red zones = OUT
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="font-medium">Move:</span>
          {['A', 'D'].map(k => (
            <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
          ))}
          <span>or</span>
          {['←', '→'].map(k => (
            <kbd key={k} className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono font-semibold">{k}</kbd>
          ))}
          <span className="text-red-400 font-medium ml-2">
            <i className="ri-alert-line mr-1" />
            Red zones = OUT
          </span>
        </div>
      )}
    </div>
  );
}
